import React from 'react';
import {
  Box,
  Typography,
  Modal,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Close,
  AccessTime,
  Person,
  Receipt,
  ShoppingCart,
  AttachMoney
} from '@mui/icons-material';
import {
  SessionTimeline,
  TimelineOrderSummary,
  formatCurrency,
  formatDuration,
  getPaymentStatusInfo
} from '../../api/invoiceApi';
import { AppIconButton } from '../ui';

interface SessionDetailModalProps {
  open: boolean;
  onClose: () => void;
  session: SessionTimeline | null;
  loading?: boolean;
}

/**
 * SessionDetailModal - Displays detailed session information with order list.
 * Shows session timeline, payment status, and individual orders.
 */
const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  open,
  onClose,
  session,
  loading = false
}) => {
  const theme = useTheme();

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'DELIVERED':
        return theme.palette.success.main;
      case 'CANCELLED':
        return theme.palette.error.main;
      case 'PENDING':
      case 'PREPARING':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 500,
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: 2,
          outline: 'none'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Session Details
          </Typography>
          <AppIconButton
            icon={<Close />}
            tooltip="Close"
            variant="ghost"
            size="small"
            onClick={onClose}
          />
        </Box>

        {/* Content */}
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(90vh - 64px)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : session ? (
            <>
              {/* Session Info */}
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {session.sessionCode}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatTime(session.startedAt)} - {session.endedAt ? formatTime(session.endedAt) : 'Active'}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={getPaymentStatusInfo(session.paymentStatus).text}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getPaymentStatusInfo(session.paymentStatus).color, 0.15),
                      color: getPaymentStatusInfo(session.paymentStatus).color,
                      fontWeight: 600
                    }}
                  />
                </Box>

                {/* Stats Grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    borderRadius: 1.5
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(session.totalAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Receipt sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {session.orders.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Orders
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDuration(session.durationMinutes)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                  </Box>
                </Box>

                {/* Customer Info */}
                {session.customerName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {session.customerName}
                    </Typography>
                    {session.customerType && (
                      <Chip
                        label={session.customerType}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Orders List */}
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Orders ({session.orders.length})
                </Typography>

                {session.orders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No orders in this session
                  </Typography>
                ) : (
                  <List disablePadding>
                    {session.orders.map((order, index) => (
                      <OrderListItem
                        key={order.id}
                        order={order}
                        isLast={index === session.orders.length - 1}
                        getStatusColor={getOrderStatusColor}
                        formatDateTime={formatDateTime}
                      />
                    ))}
                  </List>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Session not found
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

interface OrderListItemProps {
  order: TimelineOrderSummary;
  isLast: boolean;
  getStatusColor: (status: string) => string;
  formatDateTime: (isoString: string) => string;
}

const OrderListItem: React.FC<OrderListItemProps> = ({
  order,
  isLast,
  getStatusColor,
  formatDateTime
}) => {
  const theme = useTheme();

  return (
    <ListItem
      disablePadding
      sx={{
        py: 1.5,
        borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {order.customOrderId}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
              {formatCurrency(order.total)}
            </Typography>
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(order.createdAt)} · {order.itemCount} item{order.itemCount !== 1 ? 's' : ''}
            </Typography>
            <Chip
              label={order.status}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: alpha(getStatusColor(order.status), 0.1),
                color: getStatusColor(order.status)
              }}
            />
          </Box>
        }
      />
    </ListItem>
  );
};

export default SessionDetailModal;
